import Phaser from 'phaser';
import { API_BASE } from '../api';
import { loadTilesetConfig, clearTilesetCache } from '../generation/TilesetLoader';
import type { TilesetConfig } from '../generation/TilesetLoader';
import { LevelFactory } from '../generation/LevelFactory';
import { GameScene } from './GameScene';
import { CampaignState, ProgressStore } from '../state/CampaignState';

/** Globally cached tileset config, available after PreloadScene completes. */
let _tilesetConfig: TilesetConfig | null = null;

export function getTilesetConfig(): TilesetConfig | null {
  return _tilesetConfig;
}

export class PreloadScene extends Phaser.Scene {
  constructor() {
    super({ key: 'PreloadScene' });
  }

  preload(): void {
    this.load.tilemapTiledJSON('level1', 'assets/tilemaps/level1.json');
    this.load.tilemapTiledJSON('level2', 'assets/tilemaps/level2.json');
    this.load.tilemapTiledJSON('level3', 'assets/tilemaps/level3.json');
    this.load.tilemapTiledJSON('level4', 'assets/tilemaps/level4.json');
    this.load.tilemapTiledJSON('test-tile-speed', 'assets/tilemaps/test-tile-speed.json');
    this.load.image('tiles', 'assets/tilesets/tiles.png');
    this.load.image('tiles-collision', 'assets/tilesets/tiles-collision.png');
    this.load.spritesheet('soldier', 'assets/sprites/soldier.png', {
      frameWidth: 32,
      frameHeight: 32,
    });
    // Load the tileset config JSON for WFC adjacency rules + walkability metadata
    this.load.json('tileset-config', 'assets/tilesets/tileset.json');
    // Load pre-computed collision geometry for static campaign levels.
    // Phaser's JSON loader silently skips 404s so missing files are handled gracefully.
    for (let i = 1; i <= 4; i++) {
      this.load.json(`collision-level${i}`, `assets/collision/level${i}.json`);
    }
  }

  create(): void {
    // Parse and cache the tileset config
    clearTilesetCache();
    try {
      const raw = this.cache.json.get('tileset-config');
      _tilesetConfig = loadTilesetConfig(raw);
    } catch (e) {
      console.warn('PreloadScene: failed to load tileset.json —', e);
      _tilesetConfig = null;
    }

    // Read per-tile collision masks from tiles-collision.png.
    // Convention: pixel value ≥ 128 = solid, < 128 = open.
    // The image is tileColumns × tileRows tiles, each TILE_SIZE × TILE_SIZE px.
    if (_tilesetConfig) {
      try {
        applyCollisionMasksFromImage(
          this.textures.get('tiles-collision').getSourceImage() as HTMLImageElement,
          _tilesetConfig,
        );
      } catch (e) {
        console.warn('PreloadScene: could not read tiles-collision.png pixel data —', e);
      }
    }

    // URL flag: ?editor=1 goes straight to the map editor
    const params = new URLSearchParams(window.location.search);
    if (params.get('editor') === '1') {
      this.scene.start('MapEditorScene');
      return;
    }

    // URL flag: ?level=<id> loads a community level from the API
    const communityLevelId = params.get('level');
    if (communityLevelId) {
      this._loadCommunityLevel(communityLevelId);
      return;
    }

    // Restore saved campaign progress (persists across browser sessions).
    const savedMission = ProgressStore.load();
    if (savedMission !== null) {
      CampaignState.missionNumber = savedMission;
    }

    const missionNumber = CampaignState.missionNumber;
    const levelKey = `level${Math.min(missionNumber, 4)}`;
    sessionStorage.setItem('ff_current_level', levelKey);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const levelData: any = (this.cache.tilemap.get(levelKey) as any)?.data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const levelMeta: any = levelData?.metadata;

    this.scene.start('BriefingScene', {
      missionNumber,
      missionName: levelMeta?.name ?? 'Operation Freefall',
      objectives: levelMeta?.objectives?.map((o: { label: string }) => o.label)
        ?? ['Eliminate all enemies', 'Destroy enemy HQ'],
      troopCount: CampaignState.getLiving().length,
      difficulty: levelData?.difficulty ?? 'normal',
    });
  }

  private _loadCommunityLevel(levelId: string): void {
    fetch(`${API_BASE}/v1/levels/${encodeURIComponent(levelId)}`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((level: any) => {
        const tiles: number[][] = JSON.parse(level.tileData ?? '[]');
        if (!Array.isArray(tiles) || tiles.length === 0) throw new Error('empty tileData');

        const generatedMap = {
          width: tiles[0].length,
          height: tiles.length,
          tiles,
          tilesetConfig: _tilesetConfig!,
        };
        const factory = new LevelFactory();
        const tiledJson = factory.buildTiledJSON(generatedMap);
        this.cache.tilemap.add('level_generated', { format: 0, data: tiledJson });
        GameScene.useGeneratedLevel = true;

        this.scene.start('BriefingScene', {
          missionNumber: 1,
          missionName: level.title ?? 'Community Level',
          objectives: ['Eliminate all enemies'],
          troopCount: 4,
          difficulty: level.difficulty ?? 'normal',
          levelId,
        });
      })
      .catch((err) => {
        console.warn(`PreloadScene: failed to load community level "${levelId}" — falling back to campaign`, err);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const level1Data: any = (this.cache.tilemap.get('level1') as any)?.data;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const level1Meta: any = level1Data?.metadata;
        this.scene.start('BriefingScene', {
          missionNumber: 1,
          missionName: level1Meta?.name ?? 'Operation Freefall',
          objectives: level1Meta?.objectives?.map((o: { label: string }) => o.label)
            ?? ['Eliminate all enemies', 'Destroy enemy HQ'],
          troopCount: 4,
          difficulty: level1Data?.difficulty ?? 'normal',
        });
      });
  }
}

// ── Collision mask loader ─────────────────────────────────────────────────────

/**
 * Read pixel data from the tiles-collision.png and stamp a 32-element uint32
 * collisionMask array onto every tile in the config.
 *
 * Image layout must match tileset.json: columns wide, enough rows for all tiles.
 * Pixel value ≥ 128  →  solid (bit = 1).
 * Pixel value  < 128  →  open  (bit = 0).
 *
 * Falls back gracefully if the image dimensions don't match.
 */
function applyCollisionMasksFromImage(img: HTMLImageElement, config: TilesetConfig): void {
  const TILE = config.tileWidth ?? 32;
  const cols = config.columns ?? 4;

  const canvas = document.createElement('canvas');
  canvas.width  = img.naturalWidth  || img.width;
  canvas.height = img.naturalHeight || img.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) { console.warn('applyCollisionMasksFromImage: no 2D context'); return; }
  ctx.drawImage(img, 0, 0);
  const { data, width } = ctx.getImageData(0, 0, canvas.width, canvas.height);

  for (const tile of config.tiles) {
    // tile.index is 1-based; row/col are 0-based grid coordinates
    const tileCol = tile.col ?? ((tile.index - 1) % cols);
    const tileRow = tile.row ?? Math.floor((tile.index - 1) / cols);

    const ox = tileCol * TILE; // pixel offset within the image
    const oy = tileRow * TILE;

    const mask: number[] = [];
    for (let py = 0; py < TILE; py++) {
      let row = 0;
      for (let px = 0; px < TILE; px++) {
        // getImageData returns RGBA; image is grayscale so check R channel only
        const r = data[((oy + py) * width + (ox + px)) * 4];
        if (r >= 128) row |= (1 << px);
      }
      mask.push(row >>> 0); // ensure unsigned 32-bit
    }
    tile.collisionMask = mask;
  }
}
